import React from 'react';
import { motion } from 'framer-motion';
import { Clock, BarChart3, AlertTriangle, CalendarX, MessageSquareWarning } from 'lucide-react';

const painPoints = [
  {
    icon: Clock,
    pain: "Reps spend 30% of their time on CRM data entry",
    who: "Sales Reps",
    color: "from-hubspot to-orange-400",
  },
  {
    icon: BarChart3,
    pain: "Forecasts are based on gut feel, not evidence",
    who: "CRO / VP Sales",
    color: "from-primary to-primary/70",
  },
  {
    icon: AlertTriangle,
    pain: "No visibility into deal health until it's too late",
    who: "Sales Managers",
    color: "from-destructive to-red-400",
  },
  {
    icon: CalendarX,
    pain: "Meeting follow-ups fall through the cracks",
    who: "Everyone",
    color: "from-blue-500 to-blue-400",
  },
  {
    icon: MessageSquareWarning,
    pain: "Competitive intel is scattered across Slack & email",
    who: "Revenue Teams",
    color: "from-purple-500 to-purple-400",
  },
];

export const ProblemSection = () => {
  return (
    <section className="w-full py-20 md:py-32 bg-muted/30 relative overflow-hidden">
      <div className="container px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-destructive/10 text-destructive text-sm font-medium mb-6">
            The Problem
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Your CRM is <span className="text-gradient-teal">broken by design</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            It was built for managers to read, not for reps to write. That's why the data is never accurate.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {painPoints.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              viewport={{ once: true }}
              className={`group relative bg-card rounded-2xl p-6 border border-border shadow-subtle hover:shadow-elevated hover:border-destructive/20 transition-all duration-300 ${
                index === 4 ? 'md:col-span-2 lg:col-span-1' : ''
              }`}
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white mb-4`}>
                <item.icon className="w-6 h-6" />
              </div>
              <p className="text-foreground font-semibold text-lg mb-3 leading-snug">
                {item.pain}
              </p>
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-muted text-sm text-muted-foreground font-medium">
                {item.who}
              </div>
            </motion.div>
          ))}
        </div>

        {/* After statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-primary/5 border border-primary/20">
            <span className="text-2xl">→</span>
            <p className="text-lg font-semibold text-foreground">
              NoQ5 fixes all of this. <span className="text-primary">Autonomously.</span>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
